// src/pages/CuratedTripsIndex.jsx
// routechef.com/trips  ·  ready-made trips you can reshape with AI before booking. Uses the site Navbar.
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { FEATURES } from "../config/features";
import { CURATED_TRIPS } from "../curated/curatedTripsData";
import { inr } from "../creator/creatorTripEngine";

const ORANGE = "#FF5B15";
const ENABLED = FEATURES.ENABLE_CURATED_TRIPS ?? FEATURES.ENABLE_CREATOR_TRIPS;

// Card "from" price: stays (2 per room) + transfers + planning fee, before getting there.
// Activities are paid at the venue, so they're not part of it.
export const estimateFromPP = (t) => {
    let pp = t.creator_markup_pp || 0;
    t.days.forEach((d) => d.blocks.forEach((b) => {
        if (b.type === "MOVE") pp += b.price_pp || 0;
        if (b.type === "STAY") pp += ((b.fallback_pn || 0) * b.nights) / 2;
    }));
    return Math.round(pp / 100) * 100;
};

export default function CuratedTripsIndex() {
    if (!ENABLED) return <Navigate to="/" replace />;
    return (
        <div style={{ ...S.page, paddingTop: 72 }}>
            <style>{CSS}</style>

            <section style={S.hero}>
                <span style={S.pill}>Curated Trips · beta</span>
                <h1 style={{ margin: "12px 0 0", fontSize: "clamp(26px, 4vw, 34px)", color: "#0F172A", lineHeight: 1.15 }}>Trips planned for you. Yours to change.</h1>
                <p style={{ maxWidth: 560, margin: "10px auto 0", color: "#475569", lineHeight: 1.55, fontSize: 15 }}>
                    Pick a ready itinerary, then tell our AI what to change: hotels, activities, days, who's coming.
                    Travel from your city and stays are priced live.
                </p>
            </section>

            <section style={S.grid}>
                {CURATED_TRIPS.map((t) => (
                    <Link key={t.slug} to={`/trips/${t.slug}`} className="ct-card" style={S.card}>
                        <div style={{ ...S.cover, backgroundImage: t.cover ? `url(${t.cover})` : t.cover_gradient }}>
                            <span style={S.days}>{t.days.length} days</span>
                        </div>
                        <div style={{ padding: "14px 16px 16px" }}>
                            <div style={{ fontWeight: 700, fontSize: 17, color: "#0F172A" }}>{t.title}</div>
                            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>{t.subtitle}</div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                                {t.vibe.map((v) => <span key={v} style={S.tag}>{v}</span>)}
                            </div>
                            <div style={S.cardFoot}>
                                <span style={{ fontSize: 12, color: "#64748B" }}>from <b style={{ color: "#0F172A", fontSize: 16 }}>{inr(estimateFromPP(t))}</b> pp</span>
                                <span style={S.cta}><Sparkles size={13} fill={ORANGE} /> Customise →</span>
                            </div>
                        </div>
                    </Link>
                ))}
            </section>

            <footer style={{ textAlign: "center", color: "#94A3B8", fontSize: 12, padding: "24px 16px 40px" }}>
                "From" prices exclude getting there and activities (paid at the venue). You see the full price for your city on the trip page.
            </footer>
        </div>
    );
}

const S = {
    page: { minHeight: "100vh", background: "#FAFAF9", fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    top: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: "#fff", borderBottom: "1px solid #EEE" },
    pill: { fontSize: 11, fontWeight: 700, color: ORANGE, background: "#FFF1EA", padding: "4px 10px", borderRadius: 99, letterSpacing: 0.4 },
    hero: { textAlign: "center", padding: "36px 16px 24px" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 18, maxWidth: 1040, margin: "0 auto", padding: "0 16px" },
    card: { background: "#fff", borderRadius: 18, overflow: "hidden", border: "1px solid #EEE", textDecoration: "none", color: "inherit", display: "block" },
    cover: { height: 160, backgroundSize: "cover", backgroundPosition: "center", position: "relative" },
    days: { position: "absolute", left: 10, bottom: 10, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 99 },
    tag: { fontSize: 11, color: "#475569", background: "#F1F5F9", padding: "3px 9px", borderRadius: 99 },
    cardFoot: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 12, borderTop: "1px solid #F1F5F9" },
    cta: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: ORANGE },
};
const CSS = `.ct-card{transition:transform .15s, box-shadow .15s}.ct-card:hover{transform:translateY(-2px);box-shadow:0 10px 30px -12px rgba(15,23,42,.25)}`;
